import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { useProgress, activityKey, resolveStatus } from "@/stores/useProgress";
import {
  PHASES_BY_WORKSHOP,
  normalizePhaseStatuses,
  deriveWorkshopStatus,
} from "@/features/run-sequence/run-sequence-content";
import {
  WORKSHOP_PANEL_DATA,
  normalizeActivityStatuses,
} from "@/features/missionControl/workshop-panel-data";

/**
 * Cascade a workshop's RUN-SEQUENCE PHASES to all-complete (`true`) or reset
 * to the "first phase active / rest upcoming" frontier (`false`), and
 * re-derive the navigator badge from the result — phases are the single
 * source of truth the badge ({@link deriveWorkshopStatus}) reads, so it can
 * never advertise "done" for a workshop whose phases disagree.
 *
 * Used by the coarse {@link setWorkshopComplete} and by the Mission Control
 * per-activity toggle, which calls this when its activity list crosses the
 * "all done" boundary so the stepper + badge can't disagree with the cards.
 * One phase persist + one workshop persist.
 */
export function cascadePhases(
  workshopId: Workshop["id"],
  complete: boolean,
): void {
  const { setPhaseStatuses, setWorkshopStatus } = useProgress.getState();
  const phaseIds = (PHASES_BY_WORKSHOP[workshopId] ?? []).map((p) => p.id);
  const phaseStatuses = normalizePhaseStatuses(
    phaseIds,
    complete ? new Set(phaseIds) : new Set<string>(),
  );
  setPhaseStatuses(phaseStatuses);
  setWorkshopStatus(
    workshopId,
    deriveWorkshopStatus(Object.values(phaseStatuses)),
  );
}

/**
 * Cascade a workshop's MISSION-CONTROL ACTIVITIES to all-done (`true`) or
 * reset to the "first activity active / rest locked" frontier (`false`).
 * Overrides are namespaced per workshop ({@link activityKey}) because
 * activity ids repeat across workshops.
 *
 * Used by the coarse {@link setWorkshopComplete} and by the Run Sequence
 * per-phase toggle, which calls this when its phase list crosses the "all
 * done" boundary so the activity cards can't disagree with the badge. One
 * activity persist.
 */
export function cascadeActivities(
  workshopId: Workshop["id"],
  complete: boolean,
): void {
  const { setActivityStatuses } = useProgress.getState();
  const activityIds = (WORKSHOP_PANEL_DATA[workshopId]?.activities ?? []).map(
    (a) => a.id,
  );
  const activityStatuses = normalizeActivityStatuses(
    activityIds,
    complete ? new Set(activityIds) : new Set<string>(),
  );
  const updates: Record<string, (typeof activityStatuses)[string]> = {};
  for (const id of activityIds) {
    updates[activityKey(workshopId, id)] = activityStatuses[id]!;
  }
  setActivityStatuses(updates);
}

/**
 * Mark (or reopen) an ENTIRE workshop in one shot, cascading to every
 * single-source-of-truth so all three surfaces always agree:
 *
 *   - Run Sequence phases (Workshop Dashboard) → all complete, or reset
 *     to "first phase active / rest upcoming".
 *   - Mission Control activities (the workshop panel) → all done, or
 *     reset to "first activity active / rest locked".
 *   - the navigator workshop badge → re-derived from the phase rollup
 *     ({@link deriveWorkshopStatus}), never set independently.
 *
 * Both the Mission Control panel button (next to "Enter Workshop", the
 * pre-entry surface) and the HudHeader sign-off pill (in-session, on the
 * workshop dashboard) route through here. Phases and activities are two
 * parallel decompositions with non-identical ids (e.g. WS8 phases are
 * `intro/a1/a2/outcome`, activities are `p0/p1/p2/beyond`), so they can only
 * ever be coupled at this coarse "the whole workshop is / isn't done"
 * boundary — never per-item. The finer per-phase / per-activity toggles keep
 * partial progress independent, and reconcile the OTHER surface only when
 * they cross this same boundary (see {@link cascadePhases} /
 * {@link cascadeActivities}).
 *
 * Writes are batched (one phase persist + one activity persist + one
 * workshop persist), so a full cascade is three IDB writes, not 2N.
 */
export function setWorkshopComplete(
  workshopId: Workshop["id"],
  complete: boolean,
): void {
  cascadePhases(workshopId, complete);
  cascadeActivities(workshopId, complete);

  // RT-GATING — cross-workshop completion cascade. Finishing a workshop
  // opens its successor for the crew. Reopening (complete=false) never
  // re-locks the next one, so in-progress work downstream is safe.
  if (complete) unlockNextWorkshop(workshopId);
}

/**
 * Open the workshop AFTER `currentId` in {@link WORKSHOPS} order: badge →
 * "active" and its first phase → "active", so the navigator advertises it
 * and the Run Sequence stepper opens on its first phase. Override-only —
 * never mutates static content. No-op when:
 *   - there is no next workshop (the last one), or
 *   - the next workshop is already underway (effective active/done), so a
 *     re-complete can't downgrade started work, or
 *   - the crew has already set a phase override on the next workshop's
 *     first phase (don't clobber a manual edit).
 */
export function unlockNextWorkshop(currentId: Workshop["id"]): void {
  const idx = WORKSHOPS.findIndex((w) => w.id === currentId);
  if (idx < 0 || idx + 1 >= WORKSHOPS.length) return;
  const next = WORKSHOPS[idx + 1]!;

  const { workshopStatus, phaseStatus, setWorkshopStatus, setPhaseStatus } =
    useProgress.getState();

  // Effective status (override ?? static default). Don't touch a workshop
  // the crew has already opened or finished.
  const effective = resolveStatus(workshopStatus, next.id, next.status);
  if (effective === "active" || effective === "done") return;

  setWorkshopStatus(next.id, "active");

  const firstPhase = (PHASES_BY_WORKSHOP[next.id] ?? [])[0];
  if (firstPhase && phaseStatus[firstPhase.id] === undefined) {
    setPhaseStatus(firstPhase.id, "active");
  }
}
